import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

const NetflixRow = ({ title, items, type = "service" }) => {
  const rowRef = useRef(null);

  const scroll = (direction) => {
    if (rowRef.current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      rowRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <div data-testid={`netflix-row-${title?.toLowerCase().replace(/\s+/g, '-')}`} className="relative group">
      {/* Title */}
      {title && (
        <h3 className="font-display text-2xl md:text-3xl tracking-wider text-white mb-6 px-4 md:px-8">
          {title}
        </h3>
      )}

      {/* Scroll Buttons */}
      <button
        data-testid="scroll-left-btn"
        onClick={() => scroll("left")}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/60 hover:bg-black/80 text-white p-2 rounded-r-md opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        data-testid="scroll-right-btn"
        onClick={() => scroll("right")}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/60 hover:bg-black/80 text-white p-2 rounded-l-md opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Row Container */}
      <div
        ref={rowRef}
        className="flex overflow-x-auto gap-4 px-4 md:px-8 pb-8 scrollbar-hide snap-x"
      >
        {items.map((item, index) => (
          <Link
            key={item.id || index}
            to={item.href || "#"}
            data-testid={`row-item-${item.id || index}`}
            className="flex-shrink-0 snap-start"
          >
            {type === "service" ? (
              <ServiceCard item={item} />
            ) : (
              <CaseStudyCard item={item} />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

const ServiceCard = ({ item }) => (
  <div className="relative w-[300px] md:w-[400px] aspect-video bg-[#141414] rounded-md overflow-hidden netflix-card border border-transparent group">
    <img
      src={item.image}
      alt={item.title}
      className="w-full h-full object-cover"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
    
    {/* Always visible overlay (previously hover-only) */}
    <div className="absolute inset-0 bg-[#050505]/80 flex flex-col justify-end p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#00E5FF] flex items-center justify-center">
          <Play className="w-5 h-5 text-black fill-black" />
        </div>
        <span className="text-sm text-[#00E5FF] font-semibold uppercase tracking-wider">
          Learn More
        </span>
      </div>
      <h4 className="font-display text-xl md:text-2xl text-white tracking-wide mb-2">
        {item.title}
      </h4>
      <p className="text-[#B3B3B3] text-sm line-clamp-2">{item.description}</p>
    </div>
  </div>
);

const CaseStudyCard = ({ item }) => (
  <div className="relative w-[280px] md:w-[350px] aspect-[2/3] md:aspect-video bg-[#141414] rounded-md overflow-hidden netflix-card border border-transparent group">
    <img
      src={item.thumbnail}
      alt={item.title}
      className="w-full h-full object-cover"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
    
    {/* Category Badge */}
    <div className="absolute top-4 left-4">
      <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30">
        {item.category?.replace("_", " ")}
      </span>
    </div>

    {/* Hover Overlay */}
    <div className="absolute inset-0 bg-[#050505]/90 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#00E5FF] flex items-center justify-center">
          <Play className="w-5 h-5 text-black fill-black" />
        </div>
        <span className="text-sm text-[#00E5FF] font-semibold uppercase tracking-wider">
          View Case Study
        </span>
      </div>
      <span className="text-[#B3B3B3] text-xs uppercase tracking-wider mb-1">
        {item.client}
      </span>
      <h4 className="font-display text-lg md:text-xl text-white tracking-wide mb-2">
        {item.title}
      </h4>
      <p className="text-[#B3B3B3] text-sm line-clamp-2">{item.summary}</p>
    </div>

    {/* Default Content */}
    <div className="absolute bottom-0 left-0 right-0 p-4 group-hover:opacity-0 transition-opacity">
      <span className="text-[#B3B3B3] text-xs uppercase tracking-wider">
        {item.client}
      </span>
      <h4 className="font-display text-base md:text-lg text-white tracking-wide mt-1">
        {item.title}
      </h4>
    </div>
  </div>
);

export default NetflixRow;
